import type { BriefComment, Task } from '@dyspatch/core';
import { useState } from 'react';
import { Markdown } from '@/components/common/Markdown';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Finding } from '@/features/findings/Finding';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { clockTime } from '@/lib/format';
import { describeNoteEffect, notePhaseFor, notePhaseLabel } from '@/lib/notes';
import { cn } from '@/lib/utils';

/**
 * One thread per task (docs/JOB-LIFECYCLE.md 2.2): everything said about it,
 * in order, whichever phase it was said in and whoever said it. A plan comment
 * from before Go, a note dropped to the agent mid-run, the reviewer's word, and
 * the agent's own findings all sit in the same list, each labelled with its
 * phase and its author, so nobody has to know which of three places to look.
 *
 * The compose box says what a note will do *right now*, because that is the
 * one thing a person cannot see from the box itself: the same words fold into
 * the plan before Go, reach the running agent within a heartbeat during the
 * run, and go back with the PR in review. Once the task is over the box goes
 * away rather than offering a note nobody is left to read.
 *
 * An agent finding is drawn by `Finding` (shared with the job page's comments
 * region, docs/JOB-LIFECYCLE.md 2.8), which is where its one verb, File as
 * issue, lives: it asks in place before it goes outward, since an issue
 * carries the job's name onto GitHub.
 */
export function TaskNotes({
  task,
  notes,
  /**
   * The thread's own heading. The pane needs it, because it is one region of a
   * stack of them inside a single card; the page does not, because "Notes and
   * findings" is already the name of the region this sits in (issue #61).
   */
  heading = true,
}: {
  task: Task;
  notes: BriefComment[];
  heading?: boolean;
}) {
  const phase = notePhaseFor(task.status);

  return (
    // No border of its own: the pane takes the region's space from `dy-stack`
    // (issue #100), and the page (`heading` false) already sits inside its
    // own "Notes and findings" `Surface`.
    <section>
      {heading ? <h4 className="dy-eyebrow px-gutter py-1.5">Notes</h4> : null}
      {notes.length ? (
        <ul className="divide-y">
          {notes.map((note) => (
            <NoteRow key={note.id} note={note} />
          ))}
        </ul>
      ) : (
        <p className="text-fg-3 px-gutter py-2.5 text-xs">Nothing said about this task yet.</p>
      )}
      {phase ? (
        <Compose taskId={task.id} effect={describeNoteEffect(phase)} />
      ) : (
        <p className="text-fg-3 border-t px-gutter py-2 text-2xs">
          The task is {task.status}; there is nobody left to read a note on it.
        </p>
      )}
    </section>
  );
}

function NoteRow({ note }: { note: BriefComment }) {
  const fromAgent = note.authorKind === 'agent';
  return (
    <li className={cn('px-gutter py-row text-xs', fromAgent && 'bg-sunken/40')}>
      <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
        <span className="font-medium">{fromAgent ? 'the agent' : note.author}</span>
        <span className="text-fg-3 text-2xs">{notePhaseLabel(note.phase)}</span>
        <span className="text-fg-3 text-2xs" title={note.createdAt}>
          {clockTime(note.createdAt)}
        </span>
        {note.phase === 'run' && !fromAgent ? (
          <span className="text-fg-3 text-2xs">
            {note.deliveredAt ? 'delivered to the agent' : 'not yet delivered'}
          </span>
        ) : null}
      </div>
      {fromAgent ? (
        <div className="mt-0.5">
          <Finding note={note} />
        </div>
      ) : (
        <Markdown source={note.body} className="mt-0.5 max-w-prose" />
      )}
    </li>
  );
}

function Compose({ taskId, effect }: { taskId: string; effect: string }) {
  const [body, setBody] = useState('');
  const send = useHubAction((text: string) => api.noteOnTask(taskId, text), {
    onDone: () => setBody(''),
    // The box keeps what you typed and prints the refusal under it.
    inlineError: true,
  });

  return (
    <form
      className="bg-sunken border-t px-gutter py-2"
      onSubmit={(event) => {
        event.preventDefault();
        const text = body.trim();
        if (!text) return;
        send.run(text);
      }}
    >
      <div className="flex items-center gap-1.5">
        <Input
          value={body}
          onChange={(event) => setBody(event.target.value)}
          maxLength={4000}
          placeholder="Note on this task…"
          aria-label="Note on this task"
          className="flex-1"
        />
        <Button type="submit" size="sm" variant="outline" disabled={send.busy}>
          {send.busy ? 'Sending…' : 'Send'}
        </Button>
      </div>
      <p className="text-fg-3 pt-1.5 text-2xs">{effect}</p>
      {send.error ? (
        <p className="text-bad pt-1 text-xs" role="alert">
          {send.error}
        </p>
      ) : null}
    </form>
  );
}
